import React, {useEffect, useRef, useState, memo, Suspense} from "react"
import {formatTextTestIds} from "@next/utils"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest/header"
import {prefixImagePath} from "../../utils/prefixPath"
import ActiveIndicator from "../SnailItemActiveIndicator"
import {PictureComponent} from "./components"
import {PrimaryNavItem, Picture} from "../../models/primary-nav"
import connect from "./connect"
import {NAV_BAR} from "../../config/constants"
import {isPrimaryNavbarLink} from "../../utils/primaryNav"
import SnailItemContainer from "../SnailItemContainer"

export type Data = {
    index: number
    item: {
        viewType?: string
        title: string
        path: string
    }
}

export type SnailItemProps = PrimaryNavItem & {
    isActive: boolean
    index: number
    path: string
    handleClick: (event: React.MouseEvent, ref: React.RefObject<HTMLLIElement>, data: Data) => void
    handleMouseEnter: (event: React.MouseEvent, ref: React.RefObject<HTMLLIElement>, data: Data) => void
    handleKeyboardEnter: (
        event: React.KeyboardEvent<HTMLElement>,
        ref: React.RefObject<HTMLLIElement>,
        data: Data,
    ) => void
    siteUrl: string
    classNames: string
    hidden: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    deactivateIndex: () => void
    viewType?: string
}
const checkValidDimension = (value: number | null) => {
    return value !== null && value > 0
}

const SnailPictureComponent = (pictures: Picture[] | undefined, title: string, siteUrl: string) => {
    const [isError, setIsError] = useState(false)
    const isPicture = pictures && pictures?.length > 0
    let snailElement: React.ReactElement | null = null

    if (isPicture) {
        const pic = pictures?.filter(pic1 => {
            return pic1.variant === "Default"
        })[0]
        if (pic?.url?.length && checkValidDimension(pic?.height) && checkValidDimension(pic?.width)) {
            const imageUrl = prefixImagePath(siteUrl)(pic?.url) as string
            snailElement = (
                <PictureComponent
                    src={imageUrl}
                    alt={title}
                    height={pic.height}
                    width={pic.width}
                    onError={() => setIsError(true)}
                />
            )
        } else {
            snailElement = null
        }
    }
    return isError ? null : snailElement
}

export const SnailItem = memo(
    ({
        index,
        isActive,
        target,
        title,
        path,
        viewType,
        handleClick,
        handleMouseEnter,
        handleKeyboardEnter,
        linkColour,
        fontWeight,
        siteUrl,
        classNames,
        mapping,
        pin,
        hidden,
        deactivateIndex,
        templating,
        pictures,
    }: SnailItemProps) => {
        const ref = React.useRef<HTMLLIElement>(null)
        const linkRef = React.useRef<HTMLAnchorElement>(null)

        const ComponentName = useTemplatedComponent(templating, MANIFEST)

        // Return focus to primary nav when secondary nav closes
        const hasBeenActiveRef = useRef(false)
        useEffect(() => {
            if (isActive && linkRef.current) {
                hasBeenActiveRef.current = true
            } else {
                if (hasBeenActiveRef.current && linkRef.current) {
                    linkRef.current.focus()
                }
                hasBeenActiveRef.current = false
            }
        }, [isActive])

        const hijackMouseEnter = (event: React.MouseEvent): void => {
            handleMouseEnter(event, ref, {item: {path, title, viewType}, index})
        }
        const hijackClick = (event: React.MouseEvent): void => {
            if (!isPrimaryNavbarLink(viewType)) {
                handleClick(event, ref, {item: {path, title, viewType}, index})
            }
        }

        const hijackKeyEnter: React.KeyboardEventHandler<HTMLAnchorElement> = event => {
            handleKeyboardEnter(event, ref, {item: {path, title, viewType}, index})
        }

        const hijackMouseLeave = () => {
            if (isPrimaryNavbarLink(viewType)) {
                deactivateIndex()
            }
        }

        const marketingStyles = {
            linkColour,
            fontWeight,
        }

        // Note: Title is passed as data-ga-v2 and ends up translated on clients browser, only on envs that are exposed to Motionpoint.
        // This is due to Motionpoint translating actively not translating custom data attributes.
        // This only applies to SSR code (e.g. This code for Primary Nav items). If translation is needed on the client side, the client side expects it already translated (e.g. using the BFF).

        const linkProps = {
            "data-ga-v1": NAV_BAR,
            "data-ga-v2": title,
        }

        const createLink = () => {
            let link = `${siteUrl}${target}`
            if (/^((http|https):\/\/)(.*)$/.test(target)) {
                link = target
            }
            return link
        }

        const props = {
            containerRef: ref,
            isActive,
            hijackClick,
            hijackMouseEnter,
            hijackMouseLeave,
            marketingStyles,
            testId: formatTextTestIds(`meganav-primarynav-link-${title}`),
            className: classNames,
            index,
            pin,
            mapping,
            hidden,
            title,
        }

        return (
            <SnailItemContainer {...props}>
                <a
                    ref={linkRef}
                    onKeyDown={hijackKeyEnter}
                    href={createLink()}
                    title={title}
                    {...linkProps}
                    id={`meganav-link-${index}`}
                >
                    {SnailPictureComponent(pictures, title, siteUrl) || (
                        <Suspense>
                            <ComponentName
                                isActive={isActive}
                                linkColour={linkColour}
                                fontWeight={fontWeight}
                                title={title}
                            />
                        </Suspense>
                    )}
                    <ActiveIndicator isActive={isActive} />
                </a>
            </SnailItemContainer>
        )
    },
)
SnailItem.displayName = "SnailItem"

export default connect(SnailItem)
